import type {
  OnyxColor,
  OnyxIconName,
  OnyxOption,
  OnyxOptionsGroup,
} from '@onyx/angular';
import type { TransportLicenseType } from '../../auth/common/enums/business/transport-license-type';
import type { DelegationCategory } from '../../dashboard/delegations/common/enums/delegation-category';
import type { DelegationStatus } from '../../dashboard/delegations/common/enums/delegation-status';
import type { ContractType } from '../../dashboard/drivers/common/enums/contract-type';
import type { DriverSettlementMethod } from '../../dashboard/drivers/common/enums/driver-settlement-method';
import type { DriverSort } from '../../dashboard/drivers/common/enums/driver-sort';
import type { DriversLicenseCategory } from '../../dashboard/drivers/common/enums/drivers-license-category';
import type { DriverCategory } from '../../dashboard/drivers/common/interfaces/driver-category';
import type { DriverStatus } from '../../dashboard/drivers/common/interfaces/driver-status';
import type { TrailerCategory } from '../../dashboard/fleet/common/constants/trailer-categories';
import type { VehicleCategory } from '../../dashboard/fleet/common/constants/vehicle-categories';
import type { FleetSort } from '../../dashboard/fleet/common/enums/fleet-sort';
import type { FleetState } from '../../dashboard/fleet/common/enums/fleet-state';
import type { OwnershipType } from '../../dashboard/fleet/common/enums/ownership-type';
import type { SemiTrailerSize } from '../../dashboard/fleet/common/enums/semi-trailer-size';
import type { TachographMake } from '../../dashboard/fleet/common/enums/tachograph-make';
import type { TachographModel } from '../../dashboard/fleet/common/enums/tachograph-model';
import type { TechnicalPointCategory } from '../../dashboard/fleet/common/enums/technical-point-category';
import type { TrailerSize } from '../../dashboard/fleet/common/enums/trailer-size';
import type { VehiclePlanningMode } from '../../dashboard/fleet/common/enums/vehicle-planning-mode';
import type { FleetParameters } from '../../dashboard/fleet/common/interfaces/fleet';
import type { CollectiveInvoiceIssueTerm } from '../../dashboard/management-panel/contractors/common/enums/collective-invoice-issue-term';
import type { ContractorSort } from '../../dashboard/management-panel/contractors/common/enums/contractor-sort';
import type { ContractorDocumentType } from '../../dashboard/management-panel/contractors/common/enums/contractor-document-type';
import type { ContractorStatus } from '../../dashboard/management-panel/contractors/common/enums/contractor-status';
import type { ContractorType } from '../../dashboard/management-panel/contractors/common/enums/contractor-type';
import type { PaymentTermType } from '../../dashboard/management-panel/contractors/common/enums/payment-term-type';
import type { CompanyDictionaryCode } from '../../dashboard/management-panel/dictionaries/common/enums/company-dictionary-code';
import type { EmployeeRole } from '../../dashboard/management-panel/employees/common/enums/employee-role';
import type { EmployeeStatus } from '../../dashboard/management-panel/employees/common/enums/employee-status';
import type { IntegrationFeature } from '../../dashboard/management-panel/integrations/common/enums/integration-feature';
import type { IntegrationProvider } from '../../dashboard/management-panel/integrations/common/enums/integration-provider';
import type { PointOfInterestType } from '../../dashboard/management-panel/points-of-interest/common/enums/point-of-interest-type';
import type { RoadPricingType } from '../../dashboard/management-panel/route-costs-and-preferences/common/enums/road-pricing-type';
import type { AttachmentType } from '../../dashboard/orders/common/enums/attachment-type';
import type { OrderCancellationChargeParty } from '../../dashboard/orders/common/enums/order-cancellation-charge-party';
import type { OrderCancellationReason } from '../../dashboard/orders/common/enums/order-cancellation-reason';
import type { OrderCategory } from '../../dashboard/orders/common/enums/order-category';
import type { OrderDispatch } from '../../dashboard/orders/common/enums/order-dispatch';
import type { OrderPointCategory } from '../../dashboard/orders/common/enums/order-point-category';
import type { OrderPointStatus } from '../../dashboard/orders/common/enums/order-point-status';
import type { OrderPointType } from '../../dashboard/orders/common/enums/order-point-type';
import type { OrderProposalCancelationReason } from '../../dashboard/orders/common/enums/order-proposal-cancelation-reason';
import type { OrderRateType } from '../../dashboard/orders/common/enums/order-rate-type';
import type { OrderRejectReason } from '../../dashboard/orders/common/enums/order-reject-reason';
import type { OrderSemiTrailerSize } from '../../dashboard/orders/common/enums/order-semi-trailer-size';
import type { OrderSort } from '../../dashboard/orders/common/enums/order-sort';
import type { OrderStatus } from '../../dashboard/orders/common/enums/order-status';
import type { OrderTimeWindowType } from '../../dashboard/orders/common/enums/order-time-window-type';
import type { OrderParameters } from '../../dashboard/orders/common/interfaces/order';
import type { FleetType } from '../interfaces/validation/fleet-validation';
import type { AdrClass } from './adr-class';
import type { BusinessType } from './business-type';
import type { CalculateRouteFeature } from './calculate-route-feature';
import type { Country } from './country';
import type { CountryGroup } from './country-group';
import type { DriverUnavailabilityReason } from './driver-unavailability-reason';
import type { EmployeeUnavailabilityReason } from './employee-unavailability-reason';
import type { FuelType } from './fuel-type';
import type { GoodsSizeType } from './goods-size-type';
import type { InterestRateType } from './interest-rate-type';
import type { InterfaceLanguage } from './interface-language';
import type { InvoiceType } from './invoice-type';
import type { TachographWorkingMode } from './tachograph-working-mode';
import type { VatRate } from './vat-rate';

export enum DictionaryCode {
  ADR_CLASS = 'adr-class',
  ATTACHMENT_TYPE = 'attachment-type',
  BUSINESS_TYPE = 'business-type',
  CALCULATE_ROUTE_FEATURE = 'calculate-route-avoid-feature',
  CHAMBERS_NUMBER = 'chambers-number',
  COLLECTIVE_INVOICE_ISSUE_TERM = 'collective-invoice-issue-term',
  COMPANY_DICTIONARY_CODE = 'company-dictionary-code',
  CONTRACT_TYPE = 'contract-type',
  CONTRACTOR_BLOCK_REASON = 'contractor-block-reason',
  CONTRACTOR_DOCUMENT_TYPE = 'contractor-document-type',
  CONTRACTOR_SORT = 'contractor-sort',
  CONTRACTOR_STATUS = 'contractor-status',
  CONTRACTOR_TYPE = 'contractor-type',
  COUNTRIES_GROUP = 'countries-group',
  COUNTRY = 'country',
  CURRENCY = 'currency',
  DELEGATION_CATEGORY = 'delegation-category',
  DELEGATION_STATUS = 'delegation-status',
  DISCHARGE_TYPE = 'discharge-type',
  DRIVER_CATEGORY = 'driver-category',
  DRIVER_EXPERIENCE_LEVEL = 'driver-experience-level',
  DRIVER_SETTLEMENT_METHOD = 'driver-settlement-method',
  DRIVER_SORT = 'driver-sort',
  DRIVER_STATUS = 'driver-status',
  DRIVER_TECHNICAL_SKILL = 'driver-technical-skill',
  DRIVER_UNAVAILABILITY_REASON = 'driver-unavailability-reason',
  DRIVERS_LICENSE_CATEGORY = 'drivers-license-category',
  EMISSION_STANDARD = 'emission-standard',
  EMPLOYEE_ROLE = 'employee-role',
  EMPLOYEE_STATUS = 'employee-status',
  EMPLOYEE_UNAVAILABILITY_REASON = 'employee-unavailability-reason',
  FLEET_PARAMETER = 'fleet-parameter',
  FLEET_SORT = 'fleet-sort',
  FLEET_STATE = 'fleet-state',
  FUEL_TYPE = 'fuel-type',
  GOODS_SIZE_TYPE = 'goods-size-type',
  INTEREST_RATE_TYPE = 'interest-rate-type',
  INTERFACE_LANGUAGE = 'interface-language',
  INVOICE_TYPE = 'invoice-type',
  LANGUAGE = 'language',
  ORDER_CANCELLATION_CHARGE_PARTY = 'order-cancellation-charge-party',
  ORDER_CANCELLATION_REASON = 'order-cancellation-reason',
  ORDER_CATEGORY = 'order-category',
  ORDER_DISPATCH = 'order-dispatch',
  ORDER_PARAMETER = 'order-parameter',
  ORDER_POINT_STATUS = 'order-point-status',
  ORDER_POINT_TYPE = 'order-point-type',
  ORDER_PROPOSAL_CANCELATION_REASON = 'order-proposal-cancelation-reason',
  ORDER_REJECT_REASON = 'order-reject-reason',
  ORDER_SEMI_TRAILER_SIZE = 'order-semi-trailer-size',
  ORDER_SORT = 'order-sort',
  ORDER_STATUS = 'order-status',
  ORDER_TIME_WINDOW_TYPE = 'order-time-window-type',
  OWNERSHIP_TYPE = 'ownership-type',
  PAYMENT_TERM_TYPE = 'payment-term-type',
  POINT_OF_INTEREST_TYPE = 'point-of-interest-type',
  RATE_TYPE = 'rate-type',
  ROAD_PRICING_TYPE = 'road-pricing-type',
  SEMI_TRAILER_SIZE = 'semi-trailer-size',
  TACHOGRAPH_MAKE = 'tachograph-make',
  TACHOGRAPH_MODEL = 'tachograph-model',
  TACHOGRAPH_WORKING_MODE = 'tachograph-working-mode',
  TANK_CERTIFICATE = 'tank-certificate',
  TECHNICAL_POINT_CATEGORY = 'technical-point-category',
  TELEMATICS_PROVIDER = 'telematics-provider',
  TELEMATICS_FEATURE = 'telematics-feature',
  TRAILER_CATEGORY = 'trailer-category',
  TRAILER_MAKE = 'trailer-make',
  TRAILER_SIZE = 'trailer-size',
  TRANSPORT_LICENSE_TYPE = 'transport-license-type',
  VAT_RATE = 'vat-rate',
  VEHICLE_AXLE_COUNT = 'vehicle-axle-count',
  VEHICLE_CATEGORY = 'vehicle-category',
  VEHICLE_MAKE = 'vehicle-make',
  VEHICLE_PLANNING_MODE = 'vehicle-planning-mode',
  VEHICLE_TYPE = 'vehicle-type',
  VEHICLE_WEIGHT_CATEGORY = 'vehicle-weight-category',
}

export interface DictionaryResponseByCode {
  [DictionaryCode.ADR_CLASS]: OnyxOption<AdrClass>;
  [DictionaryCode.ATTACHMENT_TYPE]: OnyxOption<AttachmentType>;
  [DictionaryCode.BUSINESS_TYPE]: OnyxOption<BusinessType>;
  [DictionaryCode.CALCULATE_ROUTE_FEATURE]: OnyxOption<CalculateRouteFeature>;
  [DictionaryCode.CHAMBERS_NUMBER]: OnyxOption<number>;
  [DictionaryCode.COLLECTIVE_INVOICE_ISSUE_TERM]: OnyxOption<CollectiveInvoiceIssueTerm>;
  [DictionaryCode.COMPANY_DICTIONARY_CODE]: OnyxOption<CompanyDictionaryCode>;
  [DictionaryCode.CONTRACT_TYPE]: OnyxOption<ContractType>;
  [DictionaryCode.CONTRACTOR_BLOCK_REASON]: OnyxOption<string>;
  [DictionaryCode.CONTRACTOR_DOCUMENT_TYPE]: OnyxOption<ContractorDocumentType> & {
    color: OnyxColor;
  };
  [DictionaryCode.CONTRACTOR_SORT]: OnyxOption<ContractorSort>;
  [DictionaryCode.CONTRACTOR_STATUS]: OnyxOption<ContractorStatus>;
  [DictionaryCode.CONTRACTOR_TYPE]: OnyxOption<ContractorType>;
  [DictionaryCode.COUNTRIES_GROUP]: OnyxOption<CountryGroup>;
  [DictionaryCode.COUNTRY]: OnyxOption<Country> & {
    areaCode: string;
    currency: string | null;
    languages: string[] | null;
  };
  [DictionaryCode.CURRENCY]: OnyxOption<string> & { flag: string };
  [DictionaryCode.DELEGATION_CATEGORY]: OnyxOption<DelegationCategory> & {
    statuses: DelegationStatus[];
  };
  [DictionaryCode.DELEGATION_STATUS]: OnyxOption<DelegationStatus> & {
    color: OnyxColor;
  };
  [DictionaryCode.DISCHARGE_TYPE]: OnyxOption<string>;
  [DictionaryCode.DRIVER_CATEGORY]: OnyxOption<DriverCategory>;
  [DictionaryCode.DRIVER_EXPERIENCE_LEVEL]: OnyxOption<string>;
  [DictionaryCode.DRIVER_SETTLEMENT_METHOD]: OnyxOption<DriverSettlementMethod>;
  [DictionaryCode.DRIVER_SORT]: OnyxOption<DriverSort>;
  [DictionaryCode.DRIVER_STATUS]: OnyxOption<DriverStatus>;
  [DictionaryCode.DRIVER_TECHNICAL_SKILL]: OnyxOption<string>;
  [DictionaryCode.DRIVER_UNAVAILABILITY_REASON]: OnyxOption<DriverUnavailabilityReason>;
  [DictionaryCode.DRIVERS_LICENSE_CATEGORY]: OnyxOption<DriversLicenseCategory> & {
    requireProfessionalQualifications: boolean;
    requireDriverCard: boolean;
  };
  [DictionaryCode.EMISSION_STANDARD]: OnyxOption<string>;
  [DictionaryCode.EMPLOYEE_ROLE]: OnyxOption<EmployeeRole> & {
    color: OnyxColor;
  };
  [DictionaryCode.EMPLOYEE_STATUS]: OnyxOption<EmployeeStatus> & {
    color: OnyxColor;
  };
  [DictionaryCode.EMPLOYEE_UNAVAILABILITY_REASON]: OnyxOption<EmployeeUnavailabilityReason>;
  [DictionaryCode.FLEET_PARAMETER]: OnyxOption<keyof FleetParameters>;
  [DictionaryCode.FLEET_SORT]: OnyxOption<FleetSort>;
  [DictionaryCode.FLEET_STATE]: OnyxOption<FleetState>;
  [DictionaryCode.FUEL_TYPE]: OnyxOption<FuelType> & {
    units: [string, string];
    requireAdditionalTank: boolean;
    averageSemiTruckFuelConsumption: number;
    averageStraightTruckFuelConsumption: number;
    averageVanFuelConsumption: number;
    adBlueConsumptionCoefficient: number;
  };
  [DictionaryCode.GOODS_SIZE_TYPE]: OnyxOption<GoodsSizeType>;
  [DictionaryCode.INVOICE_TYPE]: OnyxOption<InvoiceType>;
  [DictionaryCode.INTEREST_RATE_TYPE]: OnyxOption<InterestRateType>;
  [DictionaryCode.INTERFACE_LANGUAGE]: OnyxOption<InterfaceLanguage>;
  [DictionaryCode.LANGUAGE]: OnyxOption<string>;
  [DictionaryCode.ORDER_CANCELLATION_CHARGE_PARTY]: OnyxOption<OrderCancellationChargeParty>;
  [DictionaryCode.ORDER_CANCELLATION_REASON]: OnyxOption<OrderCancellationReason>;
  [DictionaryCode.ORDER_CATEGORY]: OnyxOption<OrderCategory> & {
    statuses: OrderStatus[];
  };
  [DictionaryCode.ORDER_DISPATCH]: OnyxOption<OrderDispatch>;
  [DictionaryCode.ORDER_PARAMETER]: OnyxOption<keyof OrderParameters>;
  [DictionaryCode.ORDER_POINT_STATUS]: OnyxOption<OrderPointStatus>;
  [DictionaryCode.ORDER_POINT_TYPE]: OnyxOption<OrderPointType> & {
    icon: OnyxIconName;
    color: OnyxColor;
    category: OrderPointCategory;
    includeInOrder: boolean;
    isAlwaysOpen: boolean;
    isSystemOnly: boolean;
  };
  [DictionaryCode.ORDER_PROPOSAL_CANCELATION_REASON]: OnyxOption<OrderProposalCancelationReason>;
  [DictionaryCode.ORDER_REJECT_REASON]: OnyxOption<OrderRejectReason>;
  [DictionaryCode.ORDER_SEMI_TRAILER_SIZE]: OnyxOption<OrderSemiTrailerSize>;
  [DictionaryCode.ORDER_SORT]: OnyxOption<OrderSort>;
  [DictionaryCode.ORDER_STATUS]: OnyxOption<OrderStatus> & { color: OnyxColor };
  [DictionaryCode.ORDER_TIME_WINDOW_TYPE]: OnyxOption<OrderTimeWindowType>;
  [DictionaryCode.OWNERSHIP_TYPE]: OnyxOption<OwnershipType>;
  [DictionaryCode.PAYMENT_TERM_TYPE]: OnyxOption<PaymentTermType>;
  [DictionaryCode.POINT_OF_INTEREST_TYPE]: OnyxOption<PointOfInterestType>;
  [DictionaryCode.RATE_TYPE]: OnyxOption<OrderRateType>;
  [DictionaryCode.ROAD_PRICING_TYPE]: OnyxOption<RoadPricingType>;
  [DictionaryCode.SEMI_TRAILER_SIZE]: OnyxOption<SemiTrailerSize>;
  [DictionaryCode.TACHOGRAPH_MAKE]: OnyxOption<TachographMake>;
  [DictionaryCode.TACHOGRAPH_MODEL]: OnyxOption<TachographModel>;
  [DictionaryCode.TACHOGRAPH_WORKING_MODE]: OnyxOption<TachographWorkingMode>;
  [DictionaryCode.TANK_CERTIFICATE]: OnyxOption<string>;
  [DictionaryCode.TECHNICAL_POINT_CATEGORY]: OnyxOption<TechnicalPointCategory>;
  [DictionaryCode.TELEMATICS_FEATURE]: OnyxOption<IntegrationFeature>;
  [DictionaryCode.TELEMATICS_PROVIDER]: OnyxOption<IntegrationProvider>;
  [DictionaryCode.TRAILER_CATEGORY]: OnyxOption<TrailerCategory>;
  [DictionaryCode.TRAILER_MAKE]: OnyxOption<string>;
  [DictionaryCode.TRAILER_SIZE]: OnyxOption<TrailerSize>;
  [DictionaryCode.TRANSPORT_LICENSE_TYPE]: OnyxOption<TransportLicenseType>;
  [DictionaryCode.VAT_RATE]: OnyxOption<VatRate>;
  [DictionaryCode.VEHICLE_AXLE_COUNT]: OnyxOption<number>;
  [DictionaryCode.VEHICLE_CATEGORY]: OnyxOption<VehicleCategory>;
  [DictionaryCode.VEHICLE_MAKE]: OnyxOption<string>;
  [DictionaryCode.VEHICLE_PLANNING_MODE]: OnyxOption<VehiclePlanningMode>;
  [DictionaryCode.VEHICLE_TYPE]: OnyxOption<FleetType>;
  [DictionaryCode.VEHICLE_WEIGHT_CATEGORY]: OnyxOption<string>;
}

export interface DictionaryValueByCode {
  [DictionaryCode.CURRENCY]: OnyxOptionsGroup<string, { flag: string }>;
  [DictionaryCode.ORDER_STATUS]: OnyxOptionsGroup<
    OrderStatus,
    { color: OnyxColor }
  >;
}

export type DictionaryType<Code extends DictionaryCode> =
  Code extends keyof DictionaryValueByCode
    ? DictionaryValueByCode[Code][]
    : DictionaryResponseByCode[Code][];
